import {
  categories,
  videos,
  type Category,
  type VideoItem,
} from "@/mocks/videos";

export const PAGE_SIZE = 9;

export function getAllVideos(): VideoItem[] {
  return [...videos].sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
}

export function getVideoById(id: string | undefined): VideoItem | undefined {
  if (!id) return undefined;
  return videos.find((video) => video.id === id);
}

export function getCategoryById(id: string | undefined): Category | undefined {
  if (!id) return undefined;
  return categories.find((category) => category.id === id);
}

export function getCategoryCount(id: string): number {
  return videos.filter((video) => video.categoryId === id).length;
}

export function getVideosByCategory(categoryId: string): VideoItem[] {
  return getAllVideos().filter((video) => video.categoryId === categoryId);
}

export function getHotVideos(limit = 5): VideoItem[] {
  return [...videos].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function searchVideos(keyword: string): VideoItem[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return getAllVideos().filter((video) => {
    const category = getCategoryById(video.categoryId);
    const haystack = [
      video.title,
      video.description,
      video.episode,
      ...video.tags,
      category ? category.name : "",
      category ? category.shortName : "",
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export interface FilterOptions {
  categoryId?: string;
  timeRange?: string;
  level?: string;
  region?: string;
  sort?: "latest" | "hot";
}

export function filterVideos(options: FilterOptions): VideoItem[] {
  const { categoryId, timeRange, level, region, sort = "latest" } = options;
  const result = getAllVideos().filter((video) => {
    if (categoryId && video.categoryId !== categoryId) return false;
    if (timeRange && timeRange !== "全部时间" && video.timeRange !== timeRange) return false;
    if (level && level !== "全部等级" && video.level !== level) return false;
    if (region && region !== "全部地区" && video.region !== region) return false;
    return true;
  });
  if (sort === "hot") {
    return result.sort((a, b) => b.views - a.views);
  }
  return result;
}

export function getPopularInCategory(categoryId: string, limit = 5): VideoItem[] {
  return videos
    .filter((item) => item.categoryId === categoryId)
    .sort((a, b) => b.views - a.views)
    .slice(0, limit);
}

export function getRelatedVideos(video: VideoItem, limit = 5): VideoItem[] {
  return videos
    .filter((item) => item.categoryId === video.categoryId && item.id !== video.id)
    .sort((a, b) => b.views - a.views)
    .slice(0, limit);
}

export function paginate<T>(items: T[], page: number, pageSize = PAGE_SIZE) {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    total,
    totalPages,
    page: current,
  };
}

export function formatViews(value: number): string {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }
  return String(value);
}

export function formatDate(value: string): string {
  return value.replaceAll("-", ".");
}

export function todayUpdateCount(): number {
  const latest = getAllVideos()[0];
  if (!latest) return 0;
  return videos.filter((video) => video.uploadedAt === latest.uploadedAt).length;
}

export function totalViews(): string {
  const sum = videos.reduce((acc, video) => acc + video.views, 0);
  return formatViews(sum);
}